import React from "react";
import { Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  ptsdEarlySigns,
  ptsdLongTermSigns,
  ptsdSymptoms,
  ptsdTreatmentOptions,
} from "./ptsdData";

export const PtsdSignsSection: React.FC = () => (
  <section className="mt-12 pt-8 border-t border-border">
    <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-foreground">
      Recognizing the Signs of PTSD After a Car Accident
    </h2>

    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
        <div className="flex items-center gap-2 text-accent font-bold mb-3">
          <Clock className="h-5 w-5" />
          <span className="font-headings text-base">
            Early Warning Signs (Weeks 1–4)
          </span>
        </div>
        <ul className="space-y-2 text-xs sm:text-sm text-foreground/90">
          {ptsdEarlySigns.map((s, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-accent font-bold">•</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-foreground font-bold mb-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <span className="font-headings text-base">
            Long-Term Indicators (Month 1+)
          </span>
        </div>
        <ul className="space-y-2 text-xs sm:text-sm text-foreground/90">
          {ptsdLongTermSigns.map((s, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-accent font-bold">•</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const PtsdSymptomsSection: React.FC = () => (
  <section className="mt-12 pt-8 border-t border-border">
    <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-foreground">
      Understanding PTSD: A Brief Overview
    </h2>

    <div className="mt-6 space-y-6 text-base sm:text-lg text-foreground/90 leading-relaxed">
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h3 className="font-headings text-xl font-bold text-foreground mb-3 flex items-center gap-2">
          <span className="text-accent">
            Defining Post-Traumatic Stress Disorder
          </span>
        </h3>
        <p>
          Post-traumatic stress disorder is characterized by a range of
          distressing symptoms that persist well beyond the initial traumatic
          event. These symptoms often manifest as intrusive thoughts, vivid
          flashbacks, nightmares, and intense surges of anxiety or physical
          panic when exposed to reminders. Individuals with PTSD may also
          experience active avoidance behaviors, emotional numbness,
          irritability, and chronic hyperarousal.
        </p>
        <p className="mt-3 text-muted-foreground">
          It is important to emphasize that PTSD is not a sign of weakness or a
          character flaw. It is a biological and psychological response to an
          overwhelmingly traumatic experience. The brain's response to trauma is
          complex, and PTSD is a measurable result of the nervous system's
          struggle to process, integrate, and file away the memory of the event.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h3 className="font-headings text-xl font-bold text-foreground mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-accent" />
          Common Symptoms of PTSD
        </h3>
        <p>
          While specific symptoms vary from person to person, several hallmark
          signs are common following motor vehicle accidents:
        </p>
        <ul className="mt-4 grid gap-2.5 sm:grid-cols-2 text-sm sm:text-base">
          {ptsdSymptoms.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-1" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-muted-foreground text-sm">
          PTSD is not limited to combat veterans or survivors of violent crime.
          Traumatic injury can occur in many everyday settings, and anyone who
          has experienced or witnessed a life-threatening event is vulnerable to
          developing PTSD.
        </p>
      </div>
    </div>
  </section>
);

export const PtsdTreatmentSection: React.FC = () => (
  <section className="mt-12 pt-8 border-t border-border">
    <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-foreground">
      Seeking Professional Help for PTSD Recovery
    </h2>

    <div className="prose prose-slate max-w-none text-base sm:text-lg leading-relaxed text-foreground/90 space-y-4 mt-4">
      <p>
        If you suspect that you or a family member is living with PTSD symptoms
        following an accident, early intervention can prevent symptoms from
        becoming deeply entrenched. A comprehensive psychiatric and
        psychological assessment determines an accurate diagnosis and guides an
        individualized recovery roadmap.
      </p>
    </div>

    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {ptsdTreatmentOptions.map((opt, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-headings text-base font-bold text-foreground mb-1.5">
            {opt.title}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            {opt.desc}
          </p>
        </div>
      ))}
    </div>
  </section>
);
